<script setup lang="ts">
import { Save, X } from 'lucide-vue-next';
import { computed, watch } from 'vue';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { UserGroup } from '@/services/user_groups';

interface Props {
    open: boolean;
    editingGroup: UserGroup | null;
    userGroups: UserGroup[];
    error: string | null;
    loading: boolean;
}

interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'submit', data: { name: string; description?: string }): void;
}

interface FormData {
    name: string;
    description: string;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const formData = defineModel<FormData>('formData', {
    default: () => ({ name: '', description: '' })
});

const hasNameConflict = computed(() => {
    const trimmedName = formData.value.name.trim();
    if (!trimmedName) return false;

    return props.userGroups.some((group) => {
        // If we're editing, allow the same name for the current group
        if (props.editingGroup && group.id === props.editingGroup.id) {
            return false;
        }
        return group.name.toLowerCase() === trimmedName.toLowerCase();
    });
});

const isFormValid = computed(() => {
    return formData.value.name.trim() && !hasNameConflict.value;
});

const handleSubmit = () => {
    if (!isFormValid.value) return;

    emit('submit', {
        name: formData.value.name.trim(),
        description: formData.value.description.trim() || undefined
    });
};

const handleClose = () => {
    emit('update:open', false);
};

// Reset form when editing group changes
watch(
    () => props.editingGroup,
    (group) => {
        if (group) {
            formData.value.name = group.name;
            formData.value.description = group.description || '';
        } else {
            formData.value.name = '';
            formData.value.description = '';
        }
    },
    { immediate: true }
);
</script>

<template>
    <Dialog :open="open" @update:open="$emit('update:open', $event)">
        <DialogContent class="max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    {{ editingGroup ? 'Edit Group' : 'Create New Group' }}
                </DialogTitle>
                <DialogDescription>
                    {{
                        editingGroup
                            ? 'Update the group details below.'
                            : 'Enter the details for your new user group.'
                    }}
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4">
                <!-- Error Message -->
                <div
                    v-if="error"
                    class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                >
                    <p class="text-sm text-destructive">{{ error }}</p>
                </div>

                <form @submit.prevent="handleSubmit" class="space-y-4">
                    <div class="space-y-2">
                        <Label for="dialog-name">Group Name *</Label>
                        <Input
                            id="dialog-name"
                            v-model="formData.name"
                            type="text"
                            required
                            maxlength="50"
                            placeholder="Enter group name"
                            :class="{
                                'border-destructive focus:ring-destructive':
                                    hasNameConflict
                            }"
                        />
                        <div class="flex justify-between">
                            <p
                                v-if="hasNameConflict"
                                class="text-sm text-destructive"
                            >
                                A group with this name already exists. Please
                                choose a different name.
                            </p>
                            <p class="text-xs text-muted-foreground ml-auto">
                                {{ formData.name.length }}/50
                            </p>
                        </div>
                    </div>

                    <div class="space-y-2">
                        <Label for="dialog-description">Description</Label>
                        <Textarea
                            id="dialog-description"
                            v-model="formData.description"
                            rows="3"
                            maxlength="100"
                            placeholder="Enter group description (optional)"
                        />
                        <p class="text-xs text-muted-foreground text-right">
                            {{ formData.description.length }}/100
                        </p>
                    </div>

                    <div class="flex justify-end space-x-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            @click="handleClose"
                            :disabled="loading"
                        >
                            <X class="w-4 h-4 mr-2" />
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            :disabled="loading || !isFormValid"
                        >
                            <Save class="w-4 h-4 mr-2" />
                            {{ editingGroup ? 'Update Group' : 'Create Group' }}
                        </Button>
                    </div>
                </form>
            </div>
        </DialogContent>
    </Dialog>
</template>
